import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { PRODUCT_BY_SLUG, formatPrice } from "../lib/pulaar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the Stripe.js instance per public key to avoid loading it twice.
const stripePromiseCache = {};
function getStripePromise(pk) {
  if (!pk) return null;
  if (!stripePromiseCache[pk]) stripePromiseCache[pk] = loadStripe(pk);
  return stripePromiseCache[pk];
}

/**
 * Slow vertical scroll gallery for the left-side checkout background.
 * Fades through all product photos with a strong blur + dark overlay.
 */
function GalleryBackdrop({ product }) {
  const photos = useMemo(
    () => (product?.gallery && product.gallery.length > 0 ? product.gallery : [product?.image]).filter(Boolean),
    [product]
  );

  // Build a long vertical strip of repeated images so the auto-scroll feels infinite.
  const strip = useMemo(() => {
    if (!photos.length) return [];
    const out = [];
    for (let i = 0; i < 4; i++) out.push(...photos);
    return out;
  }, [photos]);

  const stripRef = useRef(null);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    let raf;
    let y = 0;
    const totalH = el.scrollHeight / 2; // half because we duplicated
    const step = () => {
      y += 0.18; // slow continuous scroll (px / frame)
      if (y >= totalH) y = 0;
      el.style.transform = `translate3d(0, -${y}px, 0)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [strip]);

  if (!product) return null;

  return (
    <div
      className="absolute inset-0 overflow-hidden bg-[#0E0D0B]"
      data-testid="checkout-gallery-backdrop"
      aria-hidden="true"
    >
      {/* The vertical strip — translateY animated via RAF */}
      <div ref={stripRef} className="absolute inset-x-0 top-0 will-change-transform">
        {strip.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className="w-full h-screen flex items-center justify-center px-12"
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              draggable="false"
              className="max-w-[80%] max-h-[78vh] object-contain"
              style={{ filter: "blur(8px)", opacity: 0.85 }}
            />
          </div>
        ))}
      </div>
      {/* Voile sombre par-dessus */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/55 to-black/35" />
      {/* Grain léger */}
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
           style={{
             backgroundImage:
               "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='160' height='160' filter='url(%23n)' opacity='0.6'/></svg>\")",
           }}
      />
    </div>
  );
}

function PieceMeta({ product, qty }) {
  const { t } = useTranslation();
  if (!product) return null;
  return (
    <div
      className="relative max-w-md text-[#FBF8EF] space-y-6"
      data-testid="checkout-piece-meta"
    >
      <p className="text-[10px] tracking-[0.32em] uppercase text-[#E5DFD3]">
        {t("checkout.pieceLabel")}
      </p>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.02em]">
        {product.name}
      </h1>
      <p className="text-sm md:text-base leading-[1.85] text-[#E5DFD3]/90 max-w-sm">
        {product.short}
      </p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[11px] tracking-[0.24em] uppercase text-[#E5DFD3]/85">
        <dt>{t("checkout.origin")}</dt>
        <dd className="text-[#FBF8EF]">{product.origine}</dd>
        <dt>{t("checkout.fabrication")}</dt>
        <dd className="text-[#FBF8EF]">{product.fabrication}</dd>
        <dt>{t("checkout.size")}</dt>
        <dd className="text-[#FBF8EF]">{t("cart.sizeUnique")}</dd>
        <dt>{t("checkout.quantity")}</dt>
        <dd className="text-[#FBF8EF]">{qty}</dd>
      </dl>
      <p className="text-[11px] tracking-[0.32em] uppercase pt-4 border-t border-[#E5DFD3]/20">
        {t("checkout.total")} · {formatPrice(product.price * qty)}
      </p>
    </div>
  );
}

export default function Checkout() {
  const [params] = useSearchParams();
  const slug = params.get("slug");
  const qty = Math.max(1, Math.min(5, parseInt(params.get("qty") || "1", 10)));
  const customerEmail = params.get("email") || undefined;
  const customerName = params.get("name") || undefined;
  const { t } = useTranslation();

  const product = slug ? PRODUCT_BY_SLUG[slug] : null;

  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState("");
  const [error, setError] = useState("");

  // Fetch the Stripe publishable key from the backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/config`);
        if (!cancelled) setPublishableKey(data.publishable_key || "");
      } catch {
        // ignore — error rendered later
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    (async () => {
      try {
        // Détection pays (Cloudflare/CDN headers côté backend) — pré-sélectionne dans Stripe
        let detectedCountry;
        try {
          const geoRes = await axios.get(`${API}/geo`);
          detectedCountry = geoRes.data?.country;
        } catch {
          detectedCountry = undefined;
        }
        const { data } = await axios.post(`${API}/checkout/session`, {
          product_slug: product.slug,
          size: "unique",
          quantity: qty,
          customer_email: customerEmail,
          customer_name: customerName,
          customer_country: detectedCountry,
          origin_url: window.location.origin,
          ui_mode: "embedded",
        });
        if (!data.client_secret) {
          throw new Error("Le paiement embedded n'est pas configuré côté serveur.");
        }
        if (!cancelled) setClientSecret(data.client_secret);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err.message || "Erreur paiement");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product, qty, customerEmail, customerName]);

  if (!slug) return <Navigate to="/vestiaire" replace />;
  if (!product) return <Navigate to="/vestiaire" replace />;

  return (
    <main
      data-testid="checkout-page"
      className="relative w-full bg-[#0E0D0B] text-[#FBF8EF] lg:h-screen lg:overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] xl:grid-cols-[1.1fr_0.9fr] lg:h-screen">
        {/* LEFT — galerie défilante (auto) + meta fixe par-dessus */}
        <section className="relative min-h-[55vh] lg:h-screen overflow-hidden">
          <GalleryBackdrop product={product} />
          <div className="relative h-full flex items-end lg:items-center px-6 md:px-14 lg:px-20 py-16 pt-28 lg:py-32">
            <PieceMeta product={product} qty={qty} />
          </div>
        </section>

        {/* RIGHT — paiement Pulaar + Embedded Checkout. Scrollable indépendamment. */}
        <section className="relative bg-[#FBF8EF] text-[#121212] flex flex-col lg:h-screen lg:overflow-y-auto">
          <div className="px-6 md:px-12 lg:px-16 pt-24 lg:pt-28 pb-10">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033]">
              — {t("checkout.paymentTitle")}
            </p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-[-0.02em] leading-[1.05]">
              {t("checkout.paymentSubtitle")}
            </h2>
            <p className="mt-3 text-sm text-[#4A4641] max-w-md">
              {t("checkout.paymentNote", { defaultValue: "Paiement sécurisé par Stripe. Pulaar livre dans l'Espace Européen, Royaume-Uni, États-Unis, Canada, Japon et Sénégal." })}
            </p>
          </div>

          <div className="flex-1 px-2 md:px-6 lg:px-8 pb-16">
            {error ? (
              <div
                data-testid="checkout-error"
                className="mx-4 my-8 p-6 border border-[#A33A2A]/30 bg-[#A33A2A]/5 text-[#A33A2A] text-sm"
              >
                {error}
              </div>
            ) : !clientSecret ? (
              <div
                data-testid="checkout-loading"
                className="flex items-center justify-center py-32 text-[11px] tracking-[0.32em] uppercase text-[#5C4033]"
              >
                {t("checkout.loading", { defaultValue: "Chargement du paiement…" })}
              </div>
            ) : !publishableKey ? (
              <div
                data-testid="checkout-no-pk"
                className="mx-4 my-8 p-6 border border-[#A33A2A]/30 bg-[#A33A2A]/5 text-[#A33A2A] text-sm"
              >
                {t("checkout.missingKey", { defaultValue: "Clé publique Stripe manquante côté serveur." })}
              </div>
            ) : (
              <EmbeddedCheckoutProvider
                stripe={getStripePromise(publishableKey)}
                options={{ clientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
